'use client';

import {
  addHours,
  differenceInCalendarDays,
  differenceInHours,
  isAfter,
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { paymentApi } from 'src/modules/api-clients/clients';
import { useAppFlags } from '$launchdarkly/useAppFlags';
import { type PlatformFee, PlatformFeeAlertStatus } from './types';

const PAST_DUE_ALERT_GRACE_PERIOD_HOURS = 24;
const PAST_DUE_TAKEOVER_GRACE_PERIOD_DAYS = 7;

function getPlatformFeeAlertStatus(
  platformFee: PlatformFee,
  featureFlag?: boolean,
  hasLeadTeamMember?: boolean,
): PlatformFeeAlertStatus | null {
  if (!platformFee) {
    return null;
  }

  const {
    pastDueBalance,
    activePlatformFeesPaying,
    hasDefaultPaymentMethod,
    hasValidDefaultPaymentMethod,
    oldestPastDueInvoiceDueDate,
    unpaidPastDueInvoices,
  } = platformFee;
  const isPayor = activePlatformFeesPaying && activePlatformFeesPaying > 0;
  const hasPastDueBalance = pastDueBalance && pastDueBalance > 0;
  const isPastDueForMoreThanOneDay =
    oldestPastDueInvoiceDueDate &&
    differenceInHours(
      new Date(),
      fromZonedTime(oldestPastDueInvoiceDueDate, 'America/Los_Angeles'),
    ) > PAST_DUE_ALERT_GRACE_PERIOD_HOURS;

  const isPastDueForMoreThanOneWeek =
    oldestPastDueInvoiceDueDate &&
    differenceInCalendarDays(
      new Date(),
      fromZonedTime(oldestPastDueInvoiceDueDate, 'America/Los_Angeles'),
    ) > PAST_DUE_TAKEOVER_GRACE_PERIOD_DAYS;

  // Banner priorities: PLATFORM_FEE_ENFORCEMENT > MULTIPLE_PAST_DUE_INVOICES > MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE > MISSING_PAYMENT_METHOD > INVALID_PAYMENT_METHOD > PAST_DUE_BALANCE
  // PLATFORM_FEE_ENFORCEMENT and MULTIPLE_PAST_DUE_INVOICES are shown only to Associates (!hasLeadTeamMember). Partners have AGENT and LEAD role; Associates have AGENT only.
  if (
    !hasLeadTeamMember &&
    featureFlag &&
    (unpaidPastDueInvoices ?? 0) >= 3 &&
    isPastDueForMoreThanOneWeek
  ) {
    return PlatformFeeAlertStatus.PLATFORM_FEE_ENFORCEMENT;
  }

  if (
    !hasLeadTeamMember &&
    (unpaidPastDueInvoices ?? 0) >= 2 &&
    isPastDueForMoreThanOneWeek
  ) {
    return PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES;
  }

  if (
    hasPastDueBalance &&
    !hasDefaultPaymentMethod &&
    isPastDueForMoreThanOneDay
  ) {
    return PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE;
  }

  if (isPayor && !hasDefaultPaymentMethod) {
    return PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD;
  }

  if (
    hasPastDueBalance &&
    hasDefaultPaymentMethod &&
    hasValidDefaultPaymentMethod &&
    isPastDueForMoreThanOneDay
  ) {
    return PlatformFeeAlertStatus.PAST_DUE_BALANCE;
  }

  if (isPayor && !hasValidDefaultPaymentMethod) {
    return PlatformFeeAlertStatus.INVALID_PAYMENT_METHOD;
  }

  return null;
}

export { getPlatformFeeAlertStatus };

export function useFetchPlatformFee(hasLeadTeamMember?: boolean) {
  const [data, setData] = useState<PlatformFee>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { paymentsPlatformFeeEnforcement } = useAppFlags();
  const status = useMemo(
    () =>
      getPlatformFeeAlertStatus(
        data,
        paymentsPlatformFeeEnforcement,
        hasLeadTeamMember,
      ),
    [data, paymentsPlatformFeeEnforcement, hasLeadTeamMember],
  );
  const isFetching = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    oldestPastDueInvoiceDueDate = null,
    pastDueBalance = null,
    unpaidPastDueInvoices = 0,
  } = data ?? {};

  const fetchData = useCallback(async () => {
    if (isFetching.current) return;
    setIsInitialized(true);
    isFetching.current = true;
    const response = await paymentApi.GET('/me/platform-fee');
    setData(response.data);
    isFetching.current = false;
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current && status) return; // already polling
    fetchData();
    intervalRef.current = setInterval(() => fetchData(), 60_000); // poll every 1 minute
  }, [fetchData, status]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (status) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [status, startPolling, stopPolling]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // initial fetch
  useEffectOnce(() => {
    fetchData();
  });

  return {
    status,
    pastDueBalance,
    unpaidPastDueInvoices,
    oldestPastDueInvoiceDueDate,
    isInitialized,
    startPolling,
    stopPolling,
  };
}

export async function getPaymentCenterUrl(
  status: PlatformFeeAlertStatus | null,
) {
  const { data } = await paymentApi.GET('/me/payment-portal', {
    params: {
      query: {
        returnUrl: window.location.href,
        flow:
          status !== PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD
            ? 'PORTAL'
            : 'UPDATE_PAYMENT_METHOD',
      },
    },
  });

  return data?.url;
}

const DISMISSAL_DURATION_HOURS = 24;
const DISMISSAL_KEY = `platform-fee-alert-takeover-dismissed-at`;

export function usePlatformFeeTakeover({
  status,
  oldestPastDueInvoiceDueDate,
  pastDueBalance,
  hasLeadTeamMember = false,
}: {
  status: PlatformFeeAlertStatus | null;
  oldestPastDueInvoiceDueDate: string | null;
  pastDueBalance: number | null;
  hasLeadTeamMember?: boolean;
}) {
  const dismissedTakeoverAt = JSON.parse(
    localStorage.getItem(DISMISSAL_KEY) ?? 'null',
  ) as number | null;
  const setDismissedTakeoverAt = (value: number | null) => {
    localStorage.setItem(DISMISSAL_KEY, JSON.stringify(value));
  };
  let shouldShowTakeover = false;
  const hasBeenDismissed =
    dismissedTakeoverAt &&
    !isAfter(
      new Date(),
      addHours(new Date(dismissedTakeoverAt), DISMISSAL_DURATION_HOURS),
    );
  const shouldShowMissingPaymentMethodTakeover = Boolean(
    status ===
      PlatformFeeAlertStatus.MISSING_PAYMENT_METHOD_AND_HAS_PAST_DUE_BALANCE &&
      pastDueBalance &&
      pastDueBalance > 0,
  );
  const shouldShowPastDueBalanceTakeover = Boolean(
    status === PlatformFeeAlertStatus.PAST_DUE_BALANCE &&
      oldestPastDueInvoiceDueDate &&
      differenceInCalendarDays(
        new Date(),
        fromZonedTime(oldestPastDueInvoiceDueDate, 'America/Los_Angeles'),
      ) > PAST_DUE_TAKEOVER_GRACE_PERIOD_DAYS,
  );
  const shouldShowInvalidPaymentMethodTakeover =
    status === PlatformFeeAlertStatus.INVALID_PAYMENT_METHOD;

  const shouldShowMultiplePastDueInvoicesTakeover =
    !hasLeadTeamMember &&
    status === PlatformFeeAlertStatus.MULTIPLE_PAST_DUE_INVOICES;

  const shouldShowPlatformFeeEnforcementTakeover =
    !hasLeadTeamMember &&
    status === PlatformFeeAlertStatus.PLATFORM_FEE_ENFORCEMENT;

  if (
    status &&
    (shouldShowPlatformFeeEnforcementTakeover ||
      (!hasBeenDismissed &&
        (shouldShowInvalidPaymentMethodTakeover ||
          shouldShowPastDueBalanceTakeover ||
          shouldShowMissingPaymentMethodTakeover ||
          shouldShowMultiplePastDueInvoicesTakeover)))
  ) {
    shouldShowTakeover = true;
  }

  return {
    shouldShowTakeover,
    setDismissedTakeoverAt,
  };
}
